/**
 * WeeklyTimeline - 7-day view with hourly timeline (08:00-22:00)
 * Shows scheduled workouts as time blocks
 *
 * Mobile improvements:
 * - Horizontal scroll with snap points
 * - Sticky time column
 * - Touch-friendly time slots
 */

import { useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { format, addDays, isToday } from 'date-fns';
import { tr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useFitnessStore } from '../../stores/fitnessStore';
import TimeSlotBlock from './TimeSlotBlock';
import { useIsMobile } from '../../hooks/useMediaQuery';

const HOURS = Array.from({ length: 15 }, (_, i) => i + 8); // 08:00 - 22:00
const WEEKDAYS_TR = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const WeeklyTimeline = () => {
  const {
    currentWeekStart,
    scheduledWorkouts,
    workouts,
    selectScheduledWorkoutForEdit,
  } = useFitnessStore();

  const scrollContainerRef = useRef(null);
  const isMobile = useIsMobile();

  // Scroll to today on mobile
  const scrollToToday = () => {
    if (!scrollContainerRef.current || !isMobile) return;
    const todayIndex = weekDays.findIndex(day => isToday(day));
    if (todayIndex >= 0) {
      const columnWidth = scrollContainerRef.current.scrollWidth / 7;
      scrollContainerRef.current.scrollTo({
        left: Math.max(0, (todayIndex - 1) * columnWidth),
        behavior: 'smooth'
      });
    }
  };

  // Generate 7 days of the week
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  }, [currentWeekStart]);

  // Group scheduled workouts by date and hour
  const scheduledByDateHour = useMemo(() => {
    const map = {};
    scheduledWorkouts.forEach(workout => {
      const hour = parseInt(workout.scheduled_time?.split(':')[0] || '0');
      const key = `${workout.scheduled_date}-${hour}`;
      if (!map[key]) map[key] = [];
      map[key].push(workout);
    });
    return map;
  }, [scheduledWorkouts]);

  // Group completed workouts by date
  const workoutsByDate = useMemo(() => {
    const map = {};
    workouts.forEach(workout => {
      if (!map[workout.date]) map[workout.date] = [];
      map[workout.date].push(workout);
    });
    return map;
  }, [workouts]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden"
    >
      {/* Mobile scroll hint */}
      {isMobile && (
        <div className="flex items-center justify-between px-3 py-2 bg-gray-800/30 border-b border-gray-800 text-xs text-gray-500">
          <span>Yana kaydırın</span>
          <div className="flex items-center gap-1">
            <ChevronLeft className="w-3 h-3" />
            <ChevronRight className="w-3 h-3" />
          </div>
        </div>
      )}

      {/* Scrollable container for mobile */}
      <div
        ref={scrollContainerRef}
        className={`
          ${isMobile ? 'overflow-x-auto scrollbar-hide snap-x snap-mandatory' : ''}
        `}
      >
        {/* Week header with days */}
        <div className={`
          grid bg-gray-800/50 border-b border-gray-800
          ${isMobile ? 'grid-cols-[50px_repeat(7,minmax(80px,1fr))]' : 'grid-cols-8'}
          ${isMobile ? 'min-w-[610px]' : ''}
        `}>
          {/* Time column header - sticky on mobile */}
          <div className={`
            p-2 text-center text-xs text-gray-500 border-r border-gray-800
            ${isMobile ? 'sticky left-0 bg-gray-800/90 backdrop-blur-sm z-10' : ''}
          `}>
            Saat
          </div>
          {/* Day headers */}
          {weekDays.map((day, idx) => {
            const isTodayDay = isToday(day);
            const dateStr = format(day, 'yyyy-MM-dd');
            const dayWorkouts = workoutsByDate[dateStr] || [];

            return (
              <div
                key={dateStr}
                className={`
                  p-2 sm:p-3 text-center border-r border-gray-800 last:border-r-0
                  ${isTodayDay ? 'bg-orange-500/10' : ''}
                  ${isMobile ? 'snap-center' : ''}
                `}
              >
                <div className={`text-xs font-medium ${isTodayDay ? 'text-orange-400' : 'text-gray-400'}`}>
                  {WEEKDAYS_TR[idx]}
                </div>
                <div className={`text-sm sm:text-base font-semibold ${isTodayDay ? 'text-orange-400' : 'text-white'}`}>
                  {format(day, 'd')}
                </div>
                {dayWorkouts.length > 0 && (
                  <div className="mt-1 text-2xs text-green-400">
                    {dayWorkouts.length} tamamlandı
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Timeline grid */}
        <div className="max-h-[calc(100vh-300px)] sm:max-h-[calc(100vh-280px)] overflow-y-auto">
          {HOURS.map(hour => (
            <div
              key={hour}
              className={`
                grid border-b border-gray-800/50
                ${isMobile ? 'grid-cols-[50px_repeat(7,minmax(80px,1fr))] min-w-[610px]' : 'grid-cols-8'}
                min-h-[56px] sm:min-h-[60px]
              `}
            >
              {/* Time label - sticky on mobile */}
              <div className={`
                p-2 text-xs text-gray-500 border-r border-gray-800 flex items-start justify-center
                ${isMobile ? 'sticky left-0 bg-gray-900/90 backdrop-blur-sm z-10' : ''}
              `}>
                {hour.toString().padStart(2, '0')}:00
              </div>

              {/* Day columns */}
              {weekDays.map(day => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const key = `${dateStr}-${hour}`;
                const slotWorkouts = scheduledByDateHour[key] || [];
                const isTodayDay = isToday(day);

                return (
                  <div
                    key={key}
                    className={`
                      relative p-1 sm:p-1.5 border-r border-gray-800/50 last:border-r-0
                      min-h-touch
                      ${isTodayDay ? 'bg-orange-500/5' : ''}
                    `}
                  >
                    {/* Scheduled workouts */}
                    {slotWorkouts.map(workout => (
                      <TimeSlotBlock
                        key={workout.id}
                        workout={workout}
                        onClick={() => selectScheduledWorkoutForEdit(workout)}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default WeeklyTimeline;
